import { Box } from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { axios } from '../../services/axios';
import { Text } from 'components/v4';
import FormBase from 'components/forms/new/FormBase';
import FormFieldWithTooltip from 'components/forms/new/FormFieldWithTooltip';
import {
    AllowUnlimitedRowsField,
    DayFormatField,
    HoursFormatField,
    LanguageField,
    MonthFormatField,
    SampleDateField,
    TimezoneField,
} from './formFields';
import timeZones from './timezones.json';
import { initialTimezone } from 'pages/settings/constants';

/*
 * Settings function that has a accordion for Limiting rows in invoice lines and calls table
 * Also has date and time display for the user
 */
export default function Settings() {
    const dispatch = useDispatch();

    // access to the user settings from state
    const {
        allowUnlimited,
        timeZone = initialTimezone,
        ...rest
    } = useSelector((state) => {
        return { ...state.settings };
    });

    const [modalState, setModalState] = useState({
        timeZone: initialTimezone,
        allowUnlimited: false,
    });

    const [isDirty, setIsDirty] = useState(false);

    const timeZoneList = useMemo(() => {
        const seen = new Set();
        return timeZones.reduce((result, current) => {
            current.utc.forEach((tz) => {
                if (!seen.has(tz)) {
                    seen.add(tz);
                    result.push({ timeZone: tz, id: tz });
                }
            });
            return result;
        }, []);
    }, []);

    // NOTE: migrate if there is no information in redux
    // Keep this eslint disable otherwise running into infinite loop.
    useEffect(() => {
        dispatch({ type: 'INIT_TIMEZONE' });
        setModalState({
            allowUnlimited,
            timeZone,
            ...rest,
        });
        // eslint-disable-next-line
    }, []);

    const saveSettingsMutation = useMutation({
        mutationFn: async (values) => {
            const lastUpdate = Date.now();
            const data = {
                settings: {
                    ...values,
                    lastUpdate,
                },
            };
            const jsonString = `"${JSON.stringify(data).replaceAll('"', '\\"')}"`;
            try {
                await axios.post('/adminuser/uisettings', jsonString, {
                    headers: { 'Content-Type': 'application/json' },
                });
                dispatch({
                    type: 'CHANGE_ALL_SETTINGS',
                    payload: { ...values, lastUpdate },
                });
                toast.success(
                    'Your preferences have been updated successfully',
                    {
                        autoClose: 3000,
                        closeOnClick: true,
                    },
                );
            } catch (e) {
                toast.error(
                    e.message || 'An error occurred while saving settings',
                    { autoClose: 5000, closeOnClick: true },
                );
                throw e;
            }
        },
    });

    const handleFormSubmit = (values, actions) => {
        saveSettingsMutation
            .mutateAsync(values)
            .catch(() => {
                // error already handled via toast inside mutationFn
            })
            .finally(() => {
                actions.setSubmitting(false);
            });
    };

    const formFields = (errors, touched, values, setFieldValue) => {
        return [
            <AllowUnlimitedRowsField
                key="allowUnlimited"
                errors={errors}
                touched={touched}
                values={values}
                setFieldValue={setFieldValue}
                FormFieldWithTooltip={FormFieldWithTooltip}
            />,
            <Fragment key="sample">
                {/* Date & Time Settings Section */}
                <Box flex="1" textAlign="left" mb={4}>
                    <Text fontSize="lg" fontWeight="semibold">
                        Date & Time Display Settings
                    </Text>
                </Box>

                <SampleDateField
                    fieldKey="sample"
                    values={values}
                    FormFieldWithTooltip={FormFieldWithTooltip}
                />
            </Fragment>,

            <TimezoneField
                key="timezone"
                errors={errors}
                touched={touched}
                values={values}
                setFieldValue={setFieldValue}
                timeZoneList={timeZoneList}
                FormFieldWithTooltip={FormFieldWithTooltip}
            />,

            <LanguageField
                key="language"
                errors={errors}
                touched={touched}
                values={values}
                setFieldValue={setFieldValue}
                FormFieldWithTooltip={FormFieldWithTooltip}
            />,

            <DayFormatField
                key="dayFormat"
                errors={errors}
                touched={touched}
                values={values}
                setFieldValue={setFieldValue}
                FormFieldWithTooltip={FormFieldWithTooltip}
            />,

            <MonthFormatField
                key="monthFormat"
                errors={errors}
                touched={touched}
                values={values}
                setFieldValue={setFieldValue}
                FormFieldWithTooltip={FormFieldWithTooltip}
            />,

            <HoursFormatField
                key="hoursFormat"
                errors={errors}
                touched={touched}
                values={values}
                setFieldValue={setFieldValue}
                FormFieldWithTooltip={FormFieldWithTooltip}
            />,
        ];
    };

    return (
        <Box
            sx={{
                '.chakra-form-control div div:last-child div': {
                    paddingTop: 0,
                },
            }}>
            <FormBase
                isDisabled={false}
                breadCrumbTrail={[
                    {
                        label: 'Home',
                        path: '/dashboard-services',
                    },
                    {
                        label: 'Settings',
                        path: '',
                    },
                ]}
                header="Settings"
                initialValues={modalState}
                enableReinitialize
                finalFormSubmit={handleFormSubmit}
                backLink={['dashboard-services', { state: { index: 0 } }]}
                isSubmitDisabled={!isDirty}>
                {({ errors, touched, values, setFieldValue, dirty }) => {
                    if (isDirty !== dirty) setIsDirty(dirty);
                    return formFields(errors, touched, values, setFieldValue);
                }}
            </FormBase>
        </Box>
    );
}
